import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UsageDailyPoint, UsageResponse } from "@chat-yai/shared/admin-types";
import { usageApi } from "@/api/endpoints/usage";
import { Icon } from "@/components/Icon";
import { EmptyState } from "@/components/EmptyState";

export function UsagePage(): JSX.Element {
  const usageQuery = useQuery({
    queryKey: ["usage", "current_month"],
    queryFn: () => usageApi.current(),
    staleTime: 30_000,
  });

  const data = usageQuery.data;

  return (
    <div className="page" data-testid="usage-page">
      <div className="page-head">
        <div>
          <div className="page-title">Usage</div>
          <div className="page-sub">
            This month&apos;s conversations, leads and notifications, with plan
            limit progress.
          </div>
        </div>
      </div>

      {usageQuery.isLoading ? (
        <div className="empty">Loading usage…</div>
      ) : usageQuery.isError || !data ? (
        <EmptyState
          title="Couldn't load usage"
          description="Refresh the page or sign back in."
          icon="alert"
        />
      ) : (
        <UsageBody data={data} />
      )}
    </div>
  );
}

function UsageBody({ data }: { data: UsageResponse }): JSX.Element {
  const totalDailyChat = useMemo(
    () => data.daily.reduce((acc, p) => acc + p.chat_turns, 0),
    [data.daily],
  );
  return (
    <>
      <div className="kpi-grid" data-testid="usage-summary">
        <UsageCard
          label="Chat turns"
          used={data.summary.chat_turns}
          limit={data.plan_limits.chat_monthly_messages}
          unit="messages"
          enabled
          testId="usage-chat-turns"
        />
        <UsageCard
          label="New leads"
          used={data.summary.leads_created}
          limit={null}
          unit="leads"
          enabled
          testId="usage-leads"
        />
        <UsageCard
          label="Emails sent"
          used={data.summary.emails_sent}
          limit={null}
          unit="emails"
          enabled
          testId="usage-emails"
        />
        <UsageCard
          label="Web voice minutes"
          used={data.summary.voice_minutes_used}
          limit={data.plan_limits.voice_monthly_minutes}
          unit="minutes"
          enabled={data.voice_enabled}
          testId="usage-voice"
        />
        <UsageCard
          label="Phone minutes"
          used={data.summary.phone_minutes_used}
          limit={data.plan_limits.phone_included_minutes}
          unit="minutes"
          enabled={data.phone_enabled}
          testId="usage-phone"
        />
      </div>

      <div className="card" style={{ padding: 18, marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>Last 30 days</div>
            <div className="help">
              {data.period.timezone} · {data.period.start} → {data.period.end}
            </div>
          </div>
          <div className="help">
            {totalDailyChat.toLocaleString()} chat turns · {data.summary.leads_created.toLocaleString()} leads
          </div>
        </div>
        <Sparkline
          points={data.daily}
          accessor={(p) => p.chat_turns}
          height={64}
          testId="usage-sparkline"
        />
        <UsageDailyTable daily={data.daily} />
      </div>
    </>
  );
}

interface UsageCardProps {
  label: string;
  used: number | null;
  limit: number | null;
  unit: string;
  enabled: boolean;
  testId: string;
}

function UsageCard({
  label,
  used,
  limit,
  unit,
  enabled,
  testId,
}: UsageCardProps): JSX.Element {
  if (!enabled) {
    return (
      <div className="kpi-card" data-testid={testId}>
        <div className="kpi-label">{label}</div>
        <div className="kpi-value" style={{ color: "var(--text-3)" }}>
          —
        </div>
        <div className="kpi-meta">Not enabled on your plan</div>
      </div>
    );
  }
  const value = used ?? 0;
  const pct =
    limit && limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value.toLocaleString()}</div>
      <div className="kpi-meta">
        {limit ? `of ${limit.toLocaleString()} ${unit}` : `No limit · ${unit}`}
      </div>
      {limit ? (
        <div
          aria-hidden="true"
          style={{
            marginTop: 8,
            height: 6,
            background: "var(--surface-2)",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background:
                pct >= 90
                  ? "oklch(0.6 0.18 25)"
                  : pct >= 70
                    ? "oklch(0.78 0.16 70)"
                    : "var(--accent, oklch(0.6 0.18 240))",
              transition: "width 0.4s ease",
            }}
            data-testid={`${testId}-progress`}
          />
        </div>
      ) : null}
    </div>
  );
}

interface SparklineProps {
  points: UsageDailyPoint[];
  accessor: (p: UsageDailyPoint) => number;
  height: number;
  testId?: string;
}

function Sparkline({ points, accessor, height, testId }: SparklineProps): JSX.Element {
  const w = 720;
  const h = height;
  const padding = 4;
  const values = points.map(accessor);
  const max = Math.max(1, ...values);
  const stepX = points.length > 1 ? (w - padding * 2) / (points.length - 1) : 0;
  const path = points
    .map((p, i) => {
      const x = padding + stepX * i;
      const y = padding + (h - padding * 2) * (1 - accessor(p) / max);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      height={h}
      role="img"
      aria-label="Chat turns over the last 30 days"
      data-testid={testId}
      style={{ display: "block" }}
    >
      <path
        d={path}
        fill="none"
        stroke="var(--accent, oklch(0.6 0.18 240))"
        strokeWidth={1.5}
      />
      {points.map((p, i) => {
        const x = padding + stepX * i;
        const y = padding + (h - padding * 2) * (1 - accessor(p) / max);
        return (
          <circle
            key={p.date}
            cx={x}
            cy={y}
            r={1.5}
            fill="var(--accent, oklch(0.6 0.18 240))"
          >
            <title>
              {p.date}: {accessor(p)} chat turns
            </title>
          </circle>
        );
      })}
    </svg>
  );
}

function UsageDailyTable({ daily }: { daily: UsageDailyPoint[] }): JSX.Element {
  // Show the most recent 14 days inline; the SVG sparkline already conveys
  // the 30-day shape. A click-to-expand could land in WP-H.
  const rows = daily.slice(-14).reverse();
  return (
    <div className="card" style={{ marginTop: 14, border: 0, padding: 0 }}>
      <div
        className="table-row-head"
        style={{ gridTemplateColumns: "120px 1fr 1fr 1fr" }}
      >
        <div>Date</div>
        <div>Chat turns</div>
        <div>Leads</div>
        <div>Emails</div>
      </div>
      {rows.map((p) => (
        <div
          key={p.date}
          className="list-row"
          style={{ gridTemplateColumns: "120px 1fr 1fr 1fr" }}
          data-testid={`usage-day-${p.date}`}
        >
          <div className="mono" style={{ fontSize: 12 }}>
            {p.date}
          </div>
          <div style={{ fontSize: 12 }}>{p.chat_turns.toLocaleString()}</div>
          <div style={{ fontSize: 12 }}>{p.leads.toLocaleString()}</div>
          <div style={{ fontSize: 12 }}>{p.emails.toLocaleString()}</div>
        </div>
      ))}
      {rows.length === 0 ? (
        <EmptyState
          icon="trend"
          title="No usage yet this month"
          description="Numbers fill in as soon as visitors start chatting."
          testId="empty-usage"
        />
      ) : null}
      <span className="sr-only">
        <Icon name="trend" size={12} />
      </span>
    </div>
  );
}
